import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Drawer, DrawerFooter, DrawerHeader } from '../../../../components/drawer';
import { Form } from '../../../../components/form/form';
import { useViewer } from '../../../../context/viewer';
import { LibraryExercise } from '../../exercises/types';
import {
  ExerciseMaxConversionFormSchema,
  ExerciseMaxConversionFormType,
} from '../utils';
import { ExerciseMaxConversionSheetContent } from './MaxConversionSheetContent';

type CreateExerciseMaxConversionSheetProps = {
  exercises: LibraryExercise[];
  onSubmit: (data: ExerciseMaxConversionFormType) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const CreateExerciseMaxConversionSheet = ({
  exercises,
  onSubmit,
  open,
  onOpenChange,
}: CreateExerciseMaxConversionSheetProps) => {
  const { t } = useTranslation();
  const {
    org: { settings: { enableTeamLibrary } },
    team: { selectedTeam, teams },
  } = useViewer();
  const teamName = enableTeamLibrary
    ? teams.find((t) => t.id === selectedTeam)?.name
    : undefined;

  const form = useForm<ExerciseMaxConversionFormType>({
    resolver: zodResolver(ExerciseMaxConversionFormSchema),
    defaultValues: {
      percentage: 100,
      fromLibraryExerciseId: '',
      toLibraryExerciseId: '',
    },
  });
  const { handleSubmit, reset } = form;

  const onFormSubmit = (
    exerciseMaxConversion: ExerciseMaxConversionFormType,
  ) => {
    onSubmit(exerciseMaxConversion);
    onOpenChange(false);
    reset();
  };

  return (
    <Drawer open={open} onClose={() => onOpenChange(false)} anchor="right">
      <DrawerHeader
        title={t('createConversion')}
        onClose={() => onOpenChange(false)}
      >
        {teamName && (
          <Typography variant="body2" color="text.secondary">
            {teamName}
          </Typography>
        )}
      </DrawerHeader>

      <Form {...form}>
        <form
          id="create-max-conversion-form"
          onSubmit={handleSubmit(onFormSubmit)}
          className="flex flex-col grow h-full overflow-hidden"
        >
          <div className="px-8 py-4 grow overflow-y-scroll space-y-4">
            <ExerciseMaxConversionSheetContent
              form={form}
              exercises={exercises}
              mode="create"
            />
          </div>
        </form>
      </Form>
      <DrawerFooter className="flex flex-row gap-2 w-full p-8 justify-end">
        <Button variant="outlined" onClick={() => onOpenChange(false)}>
          {t('cancel')}
        </Button>
        <Button type="submit" form="create-max-conversion-form">
          {t('save')}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};
